import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useProducts } from '../../hooks/useFirestore'
import { useCategories } from '../../hooks/useCategories'
import { useCart } from '../../hooks/useCart'
import { useBusinessRulesContext } from '../../hooks/useBusinessRulesContext'
import { getImageUrl } from '../../services/cloudinary.service'
import { getHelpSettings } from '../../services/helpSettings.service'
import { getSiteSettings } from '../../services/siteSettings.service'
import { formatCurrency } from '../../utils/helpers'
import { buildWhatsAppLinkAR } from '../../utils/whatsapp'
import Toast from '../../components/Toast'

const appendQueryParams = (url, params) => {
  if (!url) return url
  try {
    const parsed = new URL(url)
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      parsed.searchParams.set(key, String(value))
    })
    return parsed.toString()
  } catch {
    const join = url.includes('?') ? '&' : '?'
    const query = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&')
    return `${url}${join}${query}`
  }
}

const addAutoplayParams = (url) => {
  return appendQueryParams(url, {
    autoplay: 1,
    mute: 0,
    controls: 1,
    playsinline: 1
  })
}

const buildEmbedUrl = (url) => {
  const raw = String(url || '').trim()
  if (!raw) return null
  if (raw.includes('youtube.com/embed/')) return raw
  const ytMatch = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]+)/)
  if (ytMatch?.[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`
  const igMatch = raw.match(/instagram\.com\/(reel|p|tv)\/([A-Za-z0-9_-]+)/)
  if (igMatch?.[1] && igMatch?.[2]) return `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`
  const ttMatch = raw.match(/tiktok\.com\/.+\/video\/(\d+)/)
  if (ttMatch?.[1]) return `https://www.tiktok.com/embed/v2/${ttMatch[1]}`
  if (raw.includes('facebook.com')) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(raw)}&show_text=false&width=500`
  }
  return null
}

const resolveImageSource = (image) => {
  if (!image) return null
  if (typeof image === 'string') return image
  if (typeof image === 'object') return image.fileId || image.url || image.thumbnailUrl || null
  return null
}

const getVariantMedia = (product, transformations) => {
  const media = []
  const seen = new Set()
  const variants = Array.isArray(product?.variants) ? product.variants : []
  variants.forEach(variant => {
    const variantImages = Array.isArray(variant?.images) ? variant.images : []
    variantImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      const key = `image:${url}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'image', src: url })
      }
    })
    const variantVideos = Array.isArray(variant?.videos) ? variant.videos : []
    variantVideos.forEach(video => {
      const embedUrl = buildEmbedUrl(video)
      if (!embedUrl) return
      const key = `video:${embedUrl}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'video', src: embedUrl })
      }
    })
  })
  if (media.length === 0) {
    const fallbackImages = Array.isArray(product?.images) ? product.images : []
    fallbackImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      const key = `image:${url}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'image', src: url })
      }
    })
  }
  return media
}

function VariantMediaCarousel({ media, alt }) {
  const safeMedia = useMemo(() => (media.length > 0 ? media : [{ type: 'image', src: getImageUrl(null) }]), [media])
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [media])

  useEffect(() => {
    if (safeMedia.length <= 1) return
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % safeMedia.length)
    }, 3500)
    return () => clearInterval(id)
  }, [safeMedia])

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        className="flex h-full w-full transition-transform duration-500"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {safeMedia.map((item, idx) => (
          <div key={`${item.type}-${item.src}-${idx}`} className="h-full w-full flex-shrink-0">
            {item.type === 'video' ? (
              <iframe
                src={item.src}
                title={`Video ${idx + 1}`}
                className="h-full w-full pointer-events-none"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            ) : (
              <img alt={alt} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" src={item.src} loading="lazy" />
            )}
          </div>
        ))}
      </div>
      {safeMedia.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {safeMedia.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`h-2 w-2 rounded-full border border-white/80 ${idx === index ? 'bg-white' : 'bg-white/50'}`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIndex(idx)
              }}
              aria-label={`Imagen ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function FashionHome() {
  const navigate = useNavigate()
  const { addToCart } = useCart()
  const { categories } = useCategories(false)
  const { rules } = useBusinessRulesContext()
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }
  const [siteSettings, setSiteSettings] = useState(null)
  const [helpSettings, setHelpSettings] = useState(null)
  const [notification, setNotification] = useState(null)
  const { products, loading } = useProducts(
    { orderByField: 'createdAt', orderDirection: 'desc', limitResults: 8 },
    []
  )

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const data = await getSiteSettings()
        if (mounted) setSiteSettings(data)
      } catch {
        if (mounted) setSiteSettings(null)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const data = await getHelpSettings()
        if (mounted) setHelpSettings(data)
      } catch {
        if (mounted) setHelpSettings(null)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  const hero = siteSettings?.hero || {}
  const heroTitle = hero.title || 'Capas de Otoño'
  const heroSubtitle = hero.subtitle || 'Descubre nuevas siluetas, tejidos premium y tonos neutros.'
  const heroImages = useMemo(() => {
    if (Array.isArray(hero.backgroundImages)) {
      return hero.backgroundImages.filter(Boolean)
    }
    return hero.backgroundImage ? [hero.backgroundImage] : []
  }, [hero.backgroundImages, hero.backgroundImage])
  const heroVideos = useMemo(() => {
    if (!Array.isArray(hero.embeddedVideos)) return []
    return hero.embeddedVideos.filter(Boolean)
  }, [hero.embeddedVideos])
  const heroFallbackImage = hero.backgroundImage || 'https://lh3.googleusercontent.com/aida-public/AB6AXuDwUu-9Blqv2rD8Oun3iE7nuPouCF7KwMuIwaZS5DB-krk9DVA2RQ9CVuSBCh51U65X2lChJpYOiHww0-t7RGmZdCXyJdDKCv5t_JqR8g1Ubdweg_ehv3-v30ZbqyZT9GmYoYUcph-dD4Oi4NweQvyvQnpmwOWptLId0vwjPf_OS9dZLsJ5MBKQBmFxOmA6qhb3EeTH2vLLKytJJ88n2Nx2i4YZj8dXql6Nwan049aB1m6LexZatzye6HO-Nn8HJ4aUXd-Sa6CQfFA'
  const heroButtonText = hero.buttonText || 'Ver Colección'
  const heroButtonLink = hero.buttonLink || '/productos'
  const heroTextColor = hero.textColor || 'white'
  const heroSlideDurationMs = useMemo(() => {
    const raw = Number(hero.slideDurationSeconds)
    if (Number.isFinite(raw) && raw > 0) return raw * 1000
    return 5500
  }, [hero.slideDurationSeconds])
  const heroMediaDurations = useMemo(() => {
    if (hero.mediaDurations && typeof hero.mediaDurations === 'object') {
      return hero.mediaDurations
    }
    return {}
  }, [hero.mediaDurations])
  const heroMedia = useMemo(() => {
    const items = [
      ...heroImages.map((src) => {
        const raw = Number(heroMediaDurations[src])
        const durationMs = Number.isFinite(raw) && raw > 0 ? raw * 1000 : heroSlideDurationMs
        return { type: 'image', src, durationMs }
      }),
      ...heroVideos.map((video) => {
        const embedUrl = buildEmbedUrl(video)
        if (!embedUrl) return null
        const raw = Number(heroMediaDurations[video])
        const durationMs = Number.isFinite(raw) && raw > 0 ? raw * 1000 : heroSlideDurationMs
        return { type: 'video', src: embedUrl, durationMs }
      })
    ].filter(Boolean)
    if (items.length === 0) return [{ type: 'image', src: heroFallbackImage, durationMs: heroSlideDurationMs }]
    return items
  }, [heroFallbackImage, heroImages, heroMediaDurations, heroSlideDurationMs, heroVideos])
  const [heroMediaIndex, setHeroMediaIndex] = useState(0)
  const heroMediaCount = heroMedia.length
  const handleHeroPrev = () => {
    if (heroMediaCount <= 1) return
    setHeroMediaIndex(prev => (prev - 1 + heroMediaCount) % heroMediaCount)
  }
  const handleHeroNext = () => {
    if (heroMediaCount <= 1) return
    setHeroMediaIndex(prev => (prev + 1) % heroMediaCount)
  }

  const heroLines = useMemo(() => {
    if (!heroTitle) return []
    return heroTitle.split('\n')
  }, [heroTitle])

  useEffect(() => {
    setHeroMediaIndex(0)
  }, [heroMediaCount])

  useEffect(() => {
    if (heroMediaCount <= 1) return
    const duration = heroMedia[heroMediaIndex]?.durationMs ?? heroSlideDurationMs
    const id = setTimeout(() => {
      setHeroMediaIndex(prev => (prev + 1) % heroMediaCount)
    }, duration)
    return () => clearTimeout(id)
  }, [heroMedia, heroMediaCount, heroMediaIndex, heroSlideDurationMs])

  const visibleCategories = (categories || []).filter(c => c?.active !== false).slice(0, 5)
  const visibleProducts = useMemo(() => {
    const base = (products || []).filter(p => p?.active !== false)
    const filtered = outOfStockPolicy.visibility === 'hide'
      ? base.filter(p => (p?.stock || 0) > 0)
      : base
    return filtered.slice(0, 8)
  }, [outOfStockPolicy.visibility, products])

  return (
    <main className="w-full flex flex-col items-center">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'success'}
        duration={notification?.duration ?? 3000}
        actionLabel={notification?.action?.label}
        onAction={notification?.action?.onClick}
        onClose={() => setNotification(null)}
      />
      <section className="w-full max-w-[1440px] px-4 md:px-6 lg:px-12 py-6">
        <div className="relative w-full h-[520px] md:h-[650px] rounded-2xl overflow-hidden group">
          <div
            className="absolute inset-0 flex transition-transform duration-700"
            style={{ transform: `translateX(-${heroMediaIndex * 100}%)` }}
          >
            {heroMedia.map((item, index) => (
              <div key={`${item.type}-${item.src}-${index}`} className="w-full h-full flex-shrink-0">
                {item.type === 'video' ? (
                  index === heroMediaIndex ? (
                    <iframe
                      src={addAutoplayParams(item.src)}
                      title={`Hero video ${index + 1}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-900" />
                  )
                ) : (
                  <img src={item.src} alt={`Hero ${index + 1}`} className="w-full h-full object-cover" />
                )}
              </div>
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-transparent pointer-events-none" />
          {heroMedia.length > 1 && (
            <>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-[6]">
                {heroMedia.map((_, idx) => (
                  <button
                    key={`hero-dot-${idx}`}
                    type="button"
                    className={`w-2 h-2 rounded-full border border-white/70 ${idx === heroMediaIndex ? 'bg-white' : 'bg-white/60'}`}
                    onClick={() => setHeroMediaIndex(idx)}
                    aria-label={`Hero ${idx + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full border border-white/70 bg-black/30 text-white text-2xl flex items-center justify-center z-[6]"
                onClick={handleHeroPrev}
                aria-label="Anterior"
              >
                ‹
              </button>
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full border border-white/70 bg-black/30 text-white text-2xl flex items-center justify-center z-[6]"
                onClick={handleHeroNext}
                aria-label="Siguiente"
              >
                ›
              </button>
            </>
          )}
          <div className="absolute bottom-0 left-0 p-8 md:p-16 lg:p-20 max-w-xl">
            <div>
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-widest uppercase bg-white/20 backdrop-blur-md rounded-full border border-white/30" style={{ color: heroTextColor }}>
                Bienvenida
              </span>
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-[1.1] tracking-tight drop-shadow-sm" style={{ color: heroTextColor }}>
                {heroLines.length > 0 ? heroLines.map((l, idx) => (
                  <span key={`${l}-${idx}`}>
                    {l}
                    {idx < heroLines.length - 1 ? <br /> : null}
                  </span>
                )) : null}
              </h1>
              <p className="text-lg mb-8 max-w-md font-medium leading-relaxed drop-shadow-md" style={{ color: heroTextColor }}>
                {heroSubtitle}
              </p>
              <button
                type="button"
                className="bg-[#c27e70] hover:bg-[#a66a5e] text-white px-8 py-4 rounded-lg font-bold text-sm tracking-wide transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg shadow-[#c27e70]/30 flex items-center gap-2"
                onClick={() => navigate(heroButtonLink)}
              >
                {heroButtonText}
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full max-w-[1440px] px-6 lg:px-12 py-8 overflow-hidden">
        <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
          {visibleCategories.length > 0 ? (
            visibleCategories.map((c) => (
              <Link
                key={c.id}
                to={`/productos?categoria=${encodeURIComponent(c.id)}`}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#e8e6e1] whitespace-nowrap hover:border-[#c27e70] transition-colors shadow-sm"
              >
                <span className="material-symbols-outlined text-lg">checkroom</span>
                <span className="font-semibold text-sm">{c.name}</span>
              </Link>
            ))
          ) : (
            <div className="text-sm text-gray-500">No hay categorías disponibles.</div>
          )}
        </div>
      </section>

      <section className="w-full max-w-[1440px] px-6 lg:px-12 py-16 bg-white rounded-3xl my-8">
        <div className="flex justify-between items-center mb-10">
          <div>
            <h2 className="text-3xl font-bold tracking-tight mb-2">Recién Llegados</h2>
            <p className="text-gray-500">Estilos frescos justo a tiempo para la temporada.</p>
          </div>
          <Link className="text-sm font-semibold hover:text-[#c27e70] transition-colors" to="/productos">
            Ver todo
          </Link>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">Cargando productos...</div>
        ) : visibleProducts.length === 0 ? (
          <div className="text-sm text-gray-500">
            No hay productos de indumentaria publicados todavía.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
            {visibleProducts.map((p) => {
              const hasDiscount = p.specialPrice > 0 && p.specialPrice < p.price
              const price = hasDiscount ? p.specialPrice : p.price
              const media = getVariantMedia(p, { width: 800, height: 1000, crop: 'fill' })

              return (
                <div
                  key={p.id}
                  className="group flex flex-col gap-3 cursor-pointer"
                  onClick={() => navigate(`/productos/${p.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/productos/${p.id}`)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="relative w-full aspect-[3/4] bg-[#f4f3f1] rounded-lg overflow-hidden">
                    <VariantMediaCarousel media={media} alt={p.name} />
                    {hasDiscount ? (
                      <span className="absolute top-3 left-3 bg-[#c27e70] text-white text-xs font-bold px-2 py-1 rounded">
                        Rebaja
                      </span>
                    ) : null}
                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <button
                        type="button"
                        className="w-full bg-white text-black py-3 rounded-lg font-bold text-sm shadow-lg hover:bg-[#c27e70] hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-black"
                        onClick={(e) => {
                          e.stopPropagation()
                          const stock = p?.stock || 0
                          if (stock <= 0 && outOfStockPolicy.purchaseBehavior !== 'allow_purchase') {
                            if (outOfStockPolicy.purchaseBehavior === 'inquiry_only') {
                              const whatsapp = helpSettings?.contact?.socialMedia?.whatsapp
                              const url = typeof window !== 'undefined'
                                ? `${window.location.origin}/productos/${p.id}`
                                : ''
                              const text = `Hola! Quiero consultar por el producto: ${p?.name || 'Producto'}. Link: ${url}`
                              const href = buildWhatsAppLinkAR(whatsapp, text)
                              setNotification({ type: 'error', message: 'Este producto no tiene stock. Disponible solo para consulta.', duration: 5000 })
                              if (href) {
                                window.open(href, '_blank', 'noreferrer')
                              } else {
                                navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')
                              }
                              return
                            }
                            setNotification({ type: 'error', message: 'Este producto no tiene stock disponible.', duration: 5000 })
                            return
                          }
                          addToCart(p, null, 1)
                          setNotification({
                            type: 'success',
                            message: `Agregado al carrito: ${p.name}`,
                            duration: 6000,
                            action: { label: 'Ver carrito', onClick: () => navigate('/carrito') }
                          })
                        }}
                        disabled={(p?.stock || 0) <= 0 && outOfStockPolicy.purchaseBehavior === 'block_purchase'}
                      >
                        {(p?.stock || 0) <= 0 && outOfStockPolicy.purchaseBehavior === 'inquiry_only'
                          ? 'Consultar'
                          : (p?.stock || 0) <= 0 && outOfStockPolicy.purchaseBehavior !== 'allow_purchase'
                            ? (outOfStockPolicy.labelText || 'Sin stock')
                            : 'Añadir Rápido'}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between items-start gap-4">
                      <Link to={`/productos/${p.id}`} className="font-bold text-lg leading-tight group-hover:text-[#c27e70] transition-colors">
                        {p.name}
                      </Link>
                      <span className="font-medium">{formatCurrency(price)}</span>
                    </div>
                    <p className="text-sm text-gray-500">{p.brand || ' '}</p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

    </main>
  )
}

export default FashionHome
